import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, CalendarDays, TrendingUp, TrendingDown, Wallet, Building2, Loader2, Minus, Users, Star, Coins, Target, BarChart3, UserCog, Percent, Activity, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCHF = (n) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

// Fairer MoM-Vergleich: bisheriger Zeitraum vs. gleicher Zeitraum im Vormonat.
const computeTrend = (current, prev) => {
  const cur = Number(current) || 0;
  const base = Number(prev) || 0;
  if (base === 0) {
    if (cur === 0) return { pct: 0, dir: 'flat' };
    return { pct: null, dir: 'up', isNew: true };
  }
  const pct = Math.round(((cur - base) / base) * 100);
  return { pct, dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
};

const TrendPill = ({ trend, testId }) => {
  if (!trend) return null;
  const { pct, dir, isNew } = trend;
  const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
  const cls =
    dir === 'up'
      ? 'text-emerald-600'
      : dir === 'down'
      ? 'text-red-600'
      : 'text-gray-400';
  const label = isNew
    ? 'neu ggü. Vormonat'
    : `${pct > 999 ? '>+999' : pct > 0 ? '+' + pct : pct}% ggü. Vormonat`;
  return (
    <div
      className={`flex items-center gap-1 mt-1 text-[10px] font-medium ${cls}`}
      data-testid={testId}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </div>
  );
};

/**
 * Iter 339+ Studio-Dashboard-Overview Widget.
 *
 * Zeigt pro aktivem Studio 4 Mini-Karten:
 *   - Termine heute
 *   - Termine diesen Monat
 *   - Umsatz heute
 *   - Umsatz diesen Monat
 *
 * Dynamisch: jedes neue Studio erscheint automatisch sobald `is_active=true`.
 * Beim Klick auf ein Studio springt der Admin in die Studio-Detailansicht.
 */
const StudioDashboardOverview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trainerStudio, setTrainerStudio] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/studios/dashboard-overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card data-testid="studio-dashboard-overview-loading">
        <CardContent className="py-8 flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Studio-Übersicht wird geladen…
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.studios?.length) {
    return null;
  }

  const trainerRows =
    trainerStudio === 'all'
      ? data.trainers || []
      : data.trainers_by_studio?.[trainerStudio] || [];

  const tiles = (studio) => [
    {
      label: 'Termine heute',
      value: studio.today_appointments,
      icon: Calendar,
      color: 'text-green-600',
      bg: 'bg-green-50',
      testId: `studio-${studio.id}-today-appointments`,
    },
    {
      label: 'Termine Woche',
      value: `${studio.week_appointments_bisher ?? studio.week_appointments} / ${studio.week_appointments_full ?? '–'}`,
      hint: 'bisher / ganze Woche',
      icon: CalendarDays,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      testId: `studio-${studio.id}-week-appointments`,
    },
    {
      label: 'Termine Monat',
      value: `${studio.month_appointments} / ${studio.month_appointments_full ?? '–'}`,
      hint: 'bisher / ganzer Monat',
      trend: computeTrend(studio.month_appointments, studio.prev_month_appointments),
      trendTestId: `studio-${studio.id}-month-appointments-trend`,
      icon: CalendarDays,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      testId: `studio-${studio.id}-month-appointments`,
    },
    {
      label: 'Umsatz heute',
      value: fmtCHF(studio.today_revenue),
      icon: TrendingUp,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      testId: `studio-${studio.id}-today-revenue`,
    },
    {
      label: 'Umsatz Monat',
      value: fmtCHF(studio.month_revenue),
      hint: 'bezahlte Rechnungen',
      trend: computeTrend(studio.month_revenue, studio.prev_month_revenue),
      trendTestId: `studio-${studio.id}-month-revenue-trend`,
      icon: Wallet,
      color: 'text-cyan-600',
      bg: 'bg-cyan-50',
      testId: `studio-${studio.id}-month-revenue`,
    },
    {
      label: 'Umsatz IST',
      value: fmtCHF(studio.revenue_ist),
      hint: 'wahrgenommene Train-&-Pay',
      icon: Coins,
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      testId: `studio-${studio.id}-revenue-ist`,
    },
    {
      label: 'Umsatz SOLL',
      value: fmtCHF(studio.revenue_soll),
      hint: 'gebuchte Train-&-Pay',
      icon: Target,
      color: 'text-sky-600',
      bg: 'bg-sky-50',
      testId: `studio-${studio.id}-revenue-soll`,
    },
    {
      label: 'Ø Umsatz/Monat',
      value: fmtCHF(studio.avg_revenue_month),
      hint: 'letzte 12 Monate',
      icon: BarChart3,
      color: 'text-violet-600',
      bg: 'bg-violet-50',
      testId: `studio-${studio.id}-avg-revenue-month`,
    },
    {
      label: 'Probetrainings (3 Mte)',
      value: studio.trial_next_3m ?? 0,
      hint: 'nur zukünftige',
      icon: Star,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      testId: `studio-${studio.id}-trial-next-3m`,
    },
    {
      label: 'Aktive Kunden',
      value: studio.active_customers ?? 0,
      hint: 'trainieren (30 Tage)',
      icon: Users,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      testId: `studio-${studio.id}-active-customers`,
    },
  ];

  return (
    <Card data-testid="studio-dashboard-overview">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="w-5 h-5 text-gray-700" />
          Standort-Übersicht
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.studios.map((studio) => (
          <div
            key={studio.id}
            className="space-y-2"
            data-testid={`studio-block-${studio.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{studio.name}</h3>
                {studio.is_default && (
                  <Badge variant="secondary" className="text-[10px]">
                    Hauptstandort
                  </Badge>
                )}
              </div>
              <button
                onClick={() => navigate(`/studios?focus=${studio.id}`)}
                className="text-xs text-blue-600 hover:underline"
                data-testid={`studio-${studio.id}-open`}
              >
                Details →
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {tiles(studio).map((t) => (
                <div
                  key={t.label}
                  className={`${t.bg} rounded-lg p-3`}
                  data-testid={t.testId}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-600">{t.label}</p>
                    <t.icon className={`w-4 h-4 ${t.color}`} />
                  </div>
                  <p className={`text-xl font-bold mt-1 ${t.color}`}>
                    {t.value}
                  </p>
                  {t.hint && (
                    <p className="text-[10px] text-gray-500 mt-0.5">{t.hint}</p>
                  )}
                  {t.trend && <TrendPill trend={t.trend} testId={t.trendTestId} />}
                </div>
              ))}
            </div>
          </div>
        ))}

        {data.studios.length > 1 && data.overall && (
          <div
            className="pt-4 mt-4 border-t border-gray-200"
            data-testid="studio-overview-overall"
          >
            <p className="text-sm font-semibold text-gray-700 mb-2">
              🏢 Gesamt aller Standorte
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Termine heute:</span>{' '}
                <span className="font-semibold">{data.overall.today_appointments}</span>
              </div>
              <div>
                <span className="text-gray-500">Termine Woche:</span>{' '}
                <span className="font-semibold">{data.overall.week_appointments_bisher ?? data.overall.week_appointments} / {data.overall.week_appointments_full}</span>
                <span className="text-[10px] text-gray-400 ml-1">(bisher/ganz)</span>
              </div>
              <div>
                <span className="text-gray-500">Termine Monat:</span>{' '}
                <span className="font-semibold">{data.overall.month_appointments} / {data.overall.month_appointments_full}</span>
                <span className="text-[10px] text-gray-400 ml-1">(bisher/ganz)</span>
                <TrendPill trend={computeTrend(data.overall.month_appointments, data.overall.prev_month_appointments)} testId="overall-month-appointments-trend" />
              </div>
              <div>
                <span className="text-gray-500">Umsatz heute:</span>{' '}
                <span className="font-semibold">{fmtCHF(data.overall.today_revenue)}</span>
              </div>
              <div>
                <span className="text-gray-500">Umsatz Monat:</span>{' '}
                <span className="font-semibold">{fmtCHF(data.overall.month_revenue)}</span>
                <span className="text-[10px] text-gray-400 ml-1">(bezahlt)</span>
                <TrendPill trend={computeTrend(data.overall.month_revenue, data.overall.prev_month_revenue)} testId="overall-month-revenue-trend" />
              </div>
              <div data-testid="overall-revenue-ist">
                <span className="text-gray-500">Umsatz IST:</span>{' '}
                <span className="font-semibold">{fmtCHF(data.overall.revenue_ist)}</span>
                <span className="text-[10px] text-gray-400 ml-1">(wahrgenommen)</span>
              </div>
              <div data-testid="overall-revenue-soll">
                <span className="text-gray-500">Umsatz SOLL:</span>{' '}
                <span className="font-semibold">{fmtCHF(data.overall.revenue_soll)}</span>
                <span className="text-[10px] text-gray-400 ml-1">(gebucht)</span>
              </div>
              <div data-testid="overall-avg-revenue-month">
                <span className="text-gray-500">Ø Umsatz/Monat:</span>{' '}
                <span className="font-semibold">{fmtCHF(data.overall.avg_revenue_month)}</span>
                <span className="text-[10px] text-gray-400 ml-1">(12 Mte)</span>
              </div>
              <div data-testid="overall-trial-next-3m">
                <span className="text-gray-500">Probetrainings (3 Mte):</span>{' '}
                <span className="font-semibold">{data.overall.trial_next_3m ?? 0}</span>
              </div>
              <div data-testid="overall-active-customers">
                <span className="text-gray-500">Aktive Kunden:</span>{' '}
                <span className="font-semibold">{data.overall.active_customers ?? 0}</span>
              </div>
            </div>
          </div>
        )}

        {data.trainers?.length > 0 && (
          <div
            className="pt-4 mt-4 border-t border-gray-200"
            data-testid="trainer-revenue-breakdown"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <UserCog className="w-4 h-4 text-gray-600" />
                Umsatz pro Trainer
                <span className="text-[10px] font-normal text-gray-400">
                  (inkl. Punkte-Trainings)
                </span>
              </p>
              {data.studios?.length > 1 && (
                <div
                  className="flex flex-wrap gap-1"
                  data-testid="trainer-studio-filter"
                >
                  <button
                    onClick={() => setTrainerStudio('all')}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      trainerStudio === 'all'
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="trainer-studio-filter-all"
                  >
                    Alle
                  </button>
                  {data.studios.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setTrainerStudio(s.id)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        trainerStudio === s.id
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`trainer-studio-filter-${s.id}`}
                    >
                      {s.short_name || s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm min-w-[540px]"
                data-testid="trainer-revenue-table"
              >
                <thead>
                  <tr className="text-gray-500 border-b border-gray-100">
                    <th className="py-2 pr-3 font-medium text-left">Trainer</th>
                    <th className="py-2 px-2 font-medium text-right">Woche IST</th>
                    <th className="py-2 px-2 font-medium text-right">Woche SOLL</th>
                    <th className="py-2 px-2 font-medium text-right">Monat IST</th>
                    <th className="py-2 pl-2 font-medium text-right">Monat SOLL</th>
                  </tr>
                </thead>
                <tbody>
                  {trainerRows.map((t) => {
                    const now = new Date();
                    const goTrainer = () =>
                      navigate(
                        `/trainers/${t.trainer_id}?year=${now.getFullYear()}&month=${now.getMonth() + 1}`
                      );
                    return (
                    <tr
                      key={t.trainer_id}
                      onClick={goTrainer}
                      className="border-b border-gray-50 hover:bg-blue-50 cursor-pointer transition-colors"
                      data-testid={`trainer-row-${t.trainer_id}`}
                      title={`Rentabilität von ${t.name} öffnen`}
                    >
                      <td className="py-2 pr-3 font-medium text-blue-700 hover:underline text-left">
                        {t.name}
                      </td>
                      <td
                        className="py-2 px-2 text-right text-teal-700"
                        data-testid={`trainer-${t.trainer_id}-week-ist`}
                      >
                        {fmtCHF(t.week_ist)}
                      </td>
                      <td
                        className="py-2 px-2 text-right text-sky-700"
                        data-testid={`trainer-${t.trainer_id}-week-soll`}
                      >
                        {fmtCHF(t.week_soll)}
                      </td>
                      <td
                        className="py-2 px-2 text-right text-teal-700 font-semibold"
                        data-testid={`trainer-${t.trainer_id}-month-ist`}
                      >
                        {fmtCHF(t.month_ist)}
                      </td>
                      <td
                        className="py-2 pl-2 text-right text-sky-700 font-semibold"
                        data-testid={`trainer-${t.trainer_id}-month-soll`}
                      >
                        {fmtCHF(t.month_soll)}
                      </td>
                    </tr>
                    );
                  })}
                  {trainerRows.length === 0 && (
                    <tr data-testid="trainer-rows-empty">
                      <td
                        colSpan={5}
                        className="py-3 text-center text-gray-400 text-xs"
                      >
                        Keine Umsätze in diesem Zeitraum.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              IST = wahrgenommene Trainings, SOLL = alle gebuchten. Punkte-Trainings
              mit Ø-Preis pro Punkt bewertet.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate('/payroll/deductions')}
          className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 px-4 py-3 mt-2 transition-colors text-left"
          data-testid="dashboard-deductions-link"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Percent className="w-4 h-4 text-gray-600" />
            Sozialabgaben &amp; Lohnabzüge
            <span className="text-[11px] font-normal text-gray-400">AHV · ALV · NBUV · UVGZ · KTG · PK</span>
          </span>
          <span className="text-xs text-blue-600">Datensätze ansehen &amp; bearbeiten →</span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin/ai-usage')}
          className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 px-4 py-3 mt-2 transition-colors text-left"
          data-testid="dashboard-ai-usage-link"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Activity className="w-4 h-4 text-gray-600" />
            KI-Verbrauch &amp; API-Zuordnung
            <span className="text-[11px] font-normal text-gray-400">welche API für welchen Dienst</span>
          </span>
          <span className="text-xs text-blue-600">Verbrauch ansehen →</span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin/ai-budget')}
          className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 px-4 py-3 mt-2 transition-colors text-left"
          data-testid="dashboard-ai-budget-link"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Wallet className="w-4 h-4 text-gray-600" />
            KI &amp; Guthaben
            <span className="text-[11px] font-normal text-gray-400">Guthaben, Budget &amp; Aufladungen</span>
          </span>
          <span className="text-xs text-blue-600">Guthaben verwalten →</span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin/subscription')}
          className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 px-4 py-3 mt-2 transition-colors text-left"
          data-testid="dashboard-subscription-link"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Package className="w-4 h-4 text-gray-600" />
            Software-Abo
            <span className="text-[11px] font-normal text-gray-400">Module &amp; Abopreis</span>
          </span>
          <span className="text-xs text-blue-600">Abo verwalten →</span>
        </button>

        <p
          className="text-[11px] text-gray-500 leading-relaxed pt-3 mt-2 border-t border-gray-100"
          data-testid="studio-overview-legend"
        >
          <strong>Umsatz Monat</strong> = alle bezahlten Rechnungen (inkl. Pakete/Abos).{' '}
          <strong>IST / SOLL</strong> = nur Train-&amp;-Pay: IST = bereits wahrgenommene,
          SOLL = alle gebuchten Trainings dieses Monats.{' '}
          <strong>Ø Umsatz/Monat</strong> = Schnitt der letzten 12 Monate (bezahlte Rechnungen).
        </p>
      </CardContent>
    </Card>
  );
};

export default StudioDashboardOverview;
